import { useMutation, useQuery } from '@tanstack/react-query'

import {
  archiveApprovedSowToDriveApiContractAutomationDriveArchiveApprovedSowPost,
  cleanupOperatingSowTestApiContractAutomationOperatingTestProjectIdCleanupPost,
  extractDownloadedGmailSowApiContractAutomationGmailSowIntakeExtractPost,
  getContractContextApiContractAutomationContractContextGet,
  getHubspotDealsApiContractAutomationHubspotDealsGet,
  getHubspotWriteReadinessApiContractAutomationHubspotReadinessGet,
  getSlackRecipientsApiContractAutomationSlackRecipientsGet,
  listOperatingPeopleApiContractAutomationOperatingPeopleGet,
  notifyViaSlackApiContractAutomationSlackNotifyPost,
  runOperatingSowTestApiContractAutomationOperatingTestRunPost,
  syncContractContextToGoogleSheetsApiContractAutomationGoogleSheetsSyncPost,
  testGmailSowIntakeApiContractAutomationGmailSowIntakeTestPost,
  updateHubspotFromApprovedSowApiContractAutomationHubspotUpdateApprovedSowPost,
  updateOperatingFromApprovedSowApiContractAutomationOperatingUpdateApprovedSowPost,
  uploadManualSowPdfApiContractAutomationManualSowUploadExtractPost,
} from '../../generated/revenue-cash-forecasting/revenue-cash-forecasting'
import type {
  ContractAutomationHubSpotUpdateRequest,
  ContractAutomationSlackNotifyResponse,
} from '../../generated/models'
import { customInstance } from '../../services/api-client'
import type {
  ContractContext,
  EditableApprovedSowData,
  GmailSowExtractResponse,
  GmailSowIntakeResponse,
  GoogleSheetsSyncResponse,
  HubSpotUpdateConfirmation,
  HubSpotWriteReadiness,
  OperatingSowTestCleanupResponse,
  OperatingSowTestFixture,
  OperatingSowTestRun,
  OperatingPeopleResponse,
  SystemUpdateResponse,
} from './types'

export const contractAutomationKeys = {
  all: ['contract-automation'] as const,
  context: (clientName: string) => [...contractAutomationKeys.all, 'context', clientName] as const,
  gmailAttachmentPreview: (localPath?: string) => [...contractAutomationKeys.all, 'gmail-attachment-preview', localPath] as const,
  operatingPeople: () => [...contractAutomationKeys.all, 'operating-people'] as const,
  hubspotDeals: (clientName: string) => [...contractAutomationKeys.all, 'hubspot-deals', clientName] as const,
  hubspotReadiness: () => [...contractAutomationKeys.all, 'hubspot-readiness'] as const,
}

export function useHubSpotDeals(clientName: string, enabled = true) {
  return useQuery({
    queryKey: contractAutomationKeys.hubspotDeals(clientName),
    queryFn: () =>
      getHubspotDealsApiContractAutomationHubspotDealsGet({
        client_name: clientName,
      }),
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useHubSpotReadiness() {
  return useQuery<HubSpotWriteReadiness>({
    queryKey: contractAutomationKeys.hubspotReadiness(),
    queryFn: async () =>
      (await getHubspotWriteReadinessApiContractAutomationHubspotReadinessGet()) as HubSpotWriteReadiness,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useOperatingPeople(enabled = true) {
  return useQuery<OperatingPeopleResponse>({
    queryKey: contractAutomationKeys.operatingPeople(),
    queryFn: async () => {
      const response = await listOperatingPeopleApiContractAutomationOperatingPeopleGet()
      return { people: response.people || [], total: response.total }
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

export function useContractContext(clientName: string, enabled: boolean) {
  return useQuery<ContractContext>({
    queryKey: contractAutomationKeys.context(clientName),
    queryFn: async () =>
      (await getContractContextApiContractAutomationContractContextGet({
        client_name: clientName,
      })) as unknown as ContractContext,
    enabled,
    staleTime: 60_000,
    retry: 1,
  })
}

export function sendContractSlackNotification(message: string) {
  return notifyViaSlackApiContractAutomationSlackNotifyPost({ message })
}

export function getContractSlackRecipients() {
  return getSlackRecipientsApiContractAutomationSlackRecipientsGet()
}

export function useContractSlackRecipients() {
  return useQuery({
    queryKey: [...contractAutomationKeys.all, 'slack-recipients'],
    queryFn: getContractSlackRecipients,
    staleTime: 60_000,
  })
}

export function sendContractSlackNotificationToRecipients(
  message: string,
  recipientEmails?: string[],
) {
  return notifyViaSlackApiContractAutomationSlackNotifyPost({
    message,
    recipient_emails: recipientEmails?.length ? recipientEmails : undefined,
  })
}

export function useSendContractSlackNotification() {
  return useMutation<
    ContractAutomationSlackNotifyResponse,
    Error,
    { message: string; recipientEmails?: string[] }
  >({
    mutationFn: ({ message, recipientEmails }) => (
      sendContractSlackNotificationToRecipients(message, recipientEmails)
    ),
  })
}

export function useSyncContractContextToGoogleSheets() {
  return useMutation<
    GoogleSheetsSyncResponse,
    Error,
    {
      context: ContractContext
      approvedData?: EditableApprovedSowData
      spreadsheetId?: string
      spreadsheetTitle?: string
    }
  >({
    mutationFn: async ({ context, approvedData, spreadsheetId, spreadsheetTitle }) =>
      (await syncContractContextToGoogleSheetsApiContractAutomationGoogleSheetsSyncPost({
        context,
        approved_data: approvedData || undefined,
        spreadsheet_id: spreadsheetId || undefined,
        spreadsheet_title: spreadsheetTitle || undefined,
      })) as unknown as GoogleSheetsSyncResponse,
  })
}

export function useRevenueModelSheetsSync() {
  return useSyncContractContextToGoogleSheets()
}

export function useScanGmailSowIntake() {
  return useMutation<
    GmailSowIntakeResponse,
    Error,
    {
      query: string
      maxMessages?: number
      download?: boolean
    }
  >({
    mutationFn: async ({ query, maxMessages = 25, download = true }) =>
      (await testGmailSowIntakeApiContractAutomationGmailSowIntakeTestPost({
        query,
        max_messages: maxMessages,
        download,
      })) as unknown as GmailSowIntakeResponse,
  })
}

export function useExtractGmailSowAttachment() {
  return useMutation<
    GmailSowExtractResponse,
    Error,
    {
      localPath: string
      filename?: string
    }
  >({
    mutationFn: async ({ localPath, filename }) =>
      (await extractDownloadedGmailSowApiContractAutomationGmailSowIntakeExtractPost({
        local_path: localPath,
        filename,
      })) as unknown as GmailSowExtractResponse,
  })
}

export function useUploadManualSowPdf() {
  return useMutation<
    GmailSowExtractResponse,
    Error,
    {
      file: File
    }
  >({
    mutationFn: async ({ file }) =>
      (await uploadManualSowPdfApiContractAutomationManualSowUploadExtractPost({
        file,
      })) as unknown as GmailSowExtractResponse,
  })
}

export function useGmailSowAttachmentPreview(
  localPath?: string,
  filename?: string,
  enabled = false,
) {
  // Stays on the mutator directly: the endpoint streams a PDF (FileResponse),
  // which has no OpenAPI schema, so the generated client cannot type it or
  // request a blob response.
  return useQuery<Blob>({
    queryKey: contractAutomationKeys.gmailAttachmentPreview(localPath),
    queryFn: () =>
      customInstance<Blob>({
        url: '/api/contract-automation/gmail/sow-intake/attachment-preview',
        method: 'POST',
        data: {
          local_path: localPath,
          filename,
        },
        responseType: 'blob',
      }),
    enabled: enabled && Boolean(localPath),
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

export function useArchiveApprovedSowToDrive() {
  return useMutation<
    SystemUpdateResponse,
    Error,
    {
      approvedData: EditableApprovedSowData
      localPath: string
      filename?: string
    }
  >({
    mutationFn: async ({ approvedData, localPath, filename }) =>
      (await archiveApprovedSowToDriveApiContractAutomationDriveArchiveApprovedSowPost({
        approved_data: approvedData,
        local_path: localPath,
        filename,
      })) as unknown as SystemUpdateResponse,
  })
}

export function useUpdateApprovedSowSystem(system: 'hubspot' | 'operating') {
  return useMutation<
    SystemUpdateResponse,
    Error,
    {
      approvedData: EditableApprovedSowData
      confirmation?: HubSpotUpdateConfirmation
    }
  >({
    mutationFn: async ({ approvedData, confirmation }) => {
      if (system === 'hubspot') {
        const request = {
          approved_data: approvedData,
          confirmation,
        } as unknown as ContractAutomationHubSpotUpdateRequest
        return (await updateHubspotFromApprovedSowApiContractAutomationHubspotUpdateApprovedSowPost(
          request,
        )) as unknown as SystemUpdateResponse
      }
      return (await updateOperatingFromApprovedSowApiContractAutomationOperatingUpdateApprovedSowPost({
        approved_data: approvedData,
      })) as unknown as SystemUpdateResponse
    },
  })
}

export function useRunOperatingSowTest() {
  return useMutation<OperatingSowTestRun, Error, OperatingSowTestFixture>({
    mutationFn: async fixture =>
      (await runOperatingSowTestApiContractAutomationOperatingTestRunPost(
        fixture,
      )) as unknown as OperatingSowTestRun,
  })
}

export function useCleanupOperatingSowTest() {
  return useMutation<OperatingSowTestCleanupResponse, Error, string>({
    mutationFn: async projectId =>
      (await cleanupOperatingSowTestApiContractAutomationOperatingTestProjectIdCleanupPost(
        projectId,
      )) as unknown as OperatingSowTestCleanupResponse,
  })
}
